/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import * as React from "react";
import dayjs from "dayjs";
import { Else, If, Then, When } from "react-if";
import {
  Button,
  Col,
  FlexContainer,
  ModalConfirm,
  Row,
  Text,
  theme,
} from "@nordcloud/gnui";
import { DataContainer, DataItem } from "~/components/Data";
import { ACTION, PopoverControls } from "~/components/PopoverControls";
import { LoaderWrap } from "~/components/Utils";
import { dateFormat, KpiType } from "~/constants";
import { generateActionConfirmText, isNotNil, Maybe, noop } from "~/tools";
import { useKpiWidget } from "../hooks";

type KpiTarget = {
  name: string;
  startDate: string;
  endDate: string;
  id: string;
  goal: number;
  contactPerson?: Maybe<{ name: string }>;
};

type Props = {
  targetData?: Maybe<KpiTarget>;
  loading: boolean;
  kpiType: KpiType;
  editable?: boolean;
  onArchiveTarget?: (id: string) => void;
  onAddNewTarget?: () => void;
  onEditTarget?: (e?: KpiTarget | null) => void;
};

export function CurrentTarget({
  targetData,
  loading,
  kpiType,
  editable = true,
  onArchiveTarget = noop,
  onAddNewTarget = noop,
  onEditTarget = noop,
}: Props) {
  const [isModalConfirmOpen, setIsModalConfirmOpen] = React.useState(false);

  const { formattedKpiPercentage } = useKpiWidget({
    type: kpiType,
    target: targetData?.goal,
    value: undefined,
    additionalValue: undefined,
  });

  const handleArchiveModalConfirm = () => {
    onArchiveTarget(targetData?.id ?? "");
    setIsModalConfirmOpen(false);
  };

  return (
    <>
      <Text
        weight="medium"
        pb={theme.spacing.spacing01}
        style={{ borderBottom: `1px solid ${theme.color.border.border01}` }}
      >
        Active Target Details
      </Text>
      <LoaderWrap inContent loading={loading} viewBox="0 0 10 1">
        <Row>
          <Col>
            <If condition={isNotNil(targetData)}>
              <Then>
                <DataContainer
                  hasBorder
                  marginBottom={theme.spacing.spacing00}
                  css={{
                    rowGap: theme.spacing.spacing02,
                  }}
                >
                  <DataItem
                    weight="medium"
                    size="md"
                    label="Target Name"
                    value={targetData?.name ?? ""}
                    padding="0"
                  />
                  <DataItem
                    weight="medium"
                    size="md"
                    label="Target Goal"
                    value={formattedKpiPercentage}
                  />
                  <DataItem
                    weight="medium"
                    size="md"
                    label="Start Date"
                    value={formatKpiDate(targetData?.startDate)}
                  />
                  <DataItem
                    weight="medium"
                    size="md"
                    label="Review Date"
                    value={formatKpiDate(targetData?.endDate)}
                  />
                  <DataItem
                    weight="medium"
                    size="md"
                    label="Contact Person"
                    value={targetData?.contactPerson?.name ?? ""}
                  />
                </DataContainer>
              </Then>
              <Else>
                <Text color={theme.colors.danger} mt={theme.spacing.spacing02}>
                  You don’t have active KPI target
                </Text>
              </Else>
            </If>
          </Col>
          <When condition={editable}>
            <Col>
              <FlexContainer
                justifyContent="flex-end"
                gap={theme.spacing.spacing02}
                marginTop={theme.spacing.spacing02}
              >
                <Button severity="low" size="md" onClick={onAddNewTarget}>
                  Add new target
                </Button>
                <When condition={isNotNil(targetData)}>
                  <PopoverControls
                    trigger={<Button icon="menu" severity="low" size="md" />}
                    items={[
                      {
                        label: "Edit Target",
                        customIcon: "edit",
                        action: ACTION.CUSTOM_TRIGGER,
                        onClick: () => onEditTarget(targetData),
                      },
                      {
                        label: "Archive Target",
                        customIcon: "archive",
                        action: ACTION.CUSTOM_TRIGGER,
                        onClick: () => setIsModalConfirmOpen(true),
                      },
                    ]}
                  />
                </When>
              </FlexContainer>
            </Col>
          </When>
        </Row>
      </LoaderWrap>
      <ModalConfirm
        isOpen={isModalConfirmOpen}
        confirm={() => handleArchiveModalConfirm()}
        actionLabel="Archive"
        contentLabel="Archive Target"
        onClose={() => setIsModalConfirmOpen(false)}
      >
        {generateActionConfirmText(targetData?.name ?? "")("archive")()}
      </ModalConfirm>
    </>
  );
}

function formatKpiDate(date?: string) {
  return dayjs(date).format(dateFormat.dayMonthYear);
}
